import { useId, type ReactNode } from 'react';

/**
 * The theme's own checkbox and toggle.
 *
 * Simply White ships `.form-check` (a styled box with an animated tick) and
 * `.form-check.switch` (a CSS-only toggle, with size and colour variants). The portal was
 * rendering bare `<input type="checkbox">` everywhere instead — an OS control in the middle
 * of a themed page — and, worse, was expressing on/off state as a *pair* of controls: a
 * badge reading "On" next to a button reading "Turn off". A switch is one control that both
 * shows the state and sets it.
 *
 * Both require the theme's exact shape: a visually hidden input followed by a label whose
 * ::before and ::after draw the control. The label is the hit target, so it must be present
 * even when there is no visible text — hence `aria-label` on the input for the bare case.
 */

interface BaseProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  /** Visible text beside the control. Omit for a bare control in a table cell. */
  label?: ReactNode;
  /** Required when `label` is omitted, so the control is still named. */
  ariaLabel?: string;
  className?: string;
}

export function Checkbox({
  checked,
  onChange,
  disabled = false,
  label,
  ariaLabel,
  className = '',
  circle = false,
}: BaseProps & { circle?: boolean }) {
  const id = useId();
  return (
    <div className={`form-check${circle ? ' checkbox-circle' : ''} ${className}`.trim()}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        aria-label={label ? undefined : ariaLabel}
        onChange={(e) => onChange(e.target.checked)}
      />
      <label htmlFor={id}>{label}</label>
    </div>
  );
}

export type SwitchTone = 'default' | 'success' | 'warning' | 'danger';

export function Switch({
  checked,
  onChange,
  disabled = false,
  label,
  ariaLabel,
  className = '',
  tone = 'default',
  large = false,
  /** Puts the control to the right of its label — for list rows where the name leads. */
  right = false,
}: BaseProps & { tone?: SwitchTone; large?: boolean; right?: boolean }) {
  const id = useId();
  const classes = [
    'form-check',
    'switch',
    tone !== 'default' && tone,
    large && 'switch-lg',
    right && 'right',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={classes}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        role="switch"
        aria-checked={checked}
        aria-label={label ? undefined : ariaLabel}
        onChange={(e) => onChange(e.target.checked)}
      />
      <label htmlFor={id}>{label}</label>
    </div>
  );
}
